import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getLeads, getStats, deleteLead, updateLead, importLeads, getOrgFeatures } from '../lib/api';
import { LeadList } from '../components/leads/LeadList';
import { LeadDetailPanel } from '../components/leads/LeadDetailPanel';
import { Navbar } from '../components/layout/Navbar';
import { StatsBar } from '../components/dashboard/StatsBar';
import { UpcomingPaymentsWidget } from '../components/dashboard/UpcomingPaymentsWidget';
import { SuperAdminDashboard } from '../components/dashboard/SuperAdminDashboard';
import { CalendarView } from '../components/calendar/CalendarView';
import { KanbanView } from '../components/pipeline/KanbanView';
import { PaymentRemindersView } from '../components/reminders/PaymentRemindersView';
import { TasksView } from '../components/tasks/TasksView';
import { AIAssistantView } from '../components/ai/AIAssistantView';
import { ImportModal } from '../components/modals/ImportModal';
import { AddLeadModal } from '../components/modals/AddLeadModal';
import { SettingsModal } from '../components/modals/SettingsModal';
import { AdminModal } from '../components/modals/AdminModal';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '../components/ui/resizable';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { List, Columns3, Calendar, Mail, CheckSquare, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

export default function DashboardPage() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [features, setFeatures] = useState({});
  const [selectedLead, setSelectedLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [activeView, setActiveView] = useState('list'); // list, kanban, calendar, reminders

  const fetchData = useCallback(async () => {
    // Super Admin doesn't have org-scoped data, skip these calls
    if (user?.role === 'superadmin') {
      setLoading(false);
      return;
    }
    try {
      const [leadsRes, statsRes, featuresRes] = await Promise.all([
        getLeads(), 
        getStats(),
        getOrgFeatures()
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
      setFeatures(featuresRes.data.features || {});
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [user?.role]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLeadSelect = (lead) => {
    setSelectedLead(lead);
  };

  const handleLeadUpdate = async (leadId, data) => {
    try {
      const res = await updateLead(leadId, data);
      setLeads(leads.map(l => l.id === leadId ? res.data : l));
      setSelectedLead(res.data);
      toast.success('Lead updated');
      fetchData(); // Refresh stats
    } catch (err) {
      toast.error('Failed to update lead');
    }
  };

  const handleLeadDelete = async (leadId) => {
    try {
      await deleteLead(leadId);
      setLeads(leads.filter(l => l.id !== leadId));
      setSelectedLead(null);
      fetchData(); // Refresh stats
      toast.success('Lead deleted');
    } catch (err) {
      toast.error('Failed to delete lead');
    }
  };

  const handleImport = async (file) => {
    try {
      const res = await importLeads(file);
      toast.success(res.data.message);
      setShowImportModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    }
  };

  const handleLeadCreated = () => {
    setShowAddModal(false);
    fetchData();
  };

  const handleFollowupCreated = () => {
    fetchData(); // Refresh stats
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = lead.business_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (lead.email && lead.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (lead.phone && lead.phone.includes(searchQuery));
    const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  // Super Admin sees Organizations dashboard
  if (user?.role === 'superadmin') {
    return (
      <div className="h-screen w-full overflow-hidden bg-background text-foreground flex flex-col" data-testid="dashboard">
        <Navbar 
          user={user} 
          onSettingsClick={() => setShowSettingsModal(true)}
        />
        <div className="flex-1 overflow-hidden">
          <SuperAdminDashboard />
        </div>
        <SettingsModal
          open={showSettingsModal}
          onClose={() => setShowSettingsModal(false)}
        />
      </div>
    );
  }

  return (
    <div className="h-screen w-full overflow-hidden bg-background text-foreground flex flex-col" data-testid="dashboard">
      <Navbar 
        user={user} 
        onImportClick={() => setShowImportModal(true)}
        onAddClick={() => setShowAddModal(true)}
        onSettingsClick={() => setShowSettingsModal(true)}
        onAdminClick={() => setShowAdminModal(true)}
      />
      
      <StatsBar stats={stats} />

      {/* View Switcher + Upcoming Payments Widget */}
      <div className="px-4 py-2 border-b bg-background/50 flex items-center justify-between gap-4">
        <Tabs value={activeView} onValueChange={setActiveView}>
          <TabsList className="bg-muted/50">
            {features.crm !== false && (
              <TabsTrigger value="list" className="flex items-center gap-2 data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">
                <List className="w-4 h-4" />
                <span className="hidden sm:inline">List</span>
              </TabsTrigger>
            )}
            {features.kanban_view !== false && (
              <TabsTrigger value="kanban" className="flex items-center gap-2 data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">
                <Columns3 className="w-4 h-4" />
                <span className="hidden sm:inline">Pipeline</span>
              </TabsTrigger>
            )}
            {features.calendar !== false && (
              <TabsTrigger value="calendar" className="flex items-center gap-2 data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">
                <Calendar className="w-4 h-4" />
                <span className="hidden sm:inline">Calendar</span>
              </TabsTrigger>
            )}
            {features.payment_reminders && (
              <TabsTrigger value="reminders" className="flex items-center gap-2 data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">
                <Mail className="w-4 h-4" />
                <span className="hidden sm:inline">Reminders</span>
              </TabsTrigger>
            )}
            {features.tasks !== false && (
              <TabsTrigger value="tasks" className="flex items-center gap-2 data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">
                <CheckSquare className="w-4 h-4" />
                <span className="hidden sm:inline">Tasks</span>
              </TabsTrigger>
            )}
            {features.ai_assistant && (
              <TabsTrigger value="ai" className="flex items-center gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-500 data-[state=active]:to-pink-500 data-[state=active]:text-white">
                <Sparkles className="w-4 h-4" />
                <span className="hidden sm:inline">AI</span>
              </TabsTrigger>
            )}
          </TabsList>
        </Tabs>
        
        {/* Compact Upcoming Payments Summary */}
        {features.calendar !== false && activeView !== 'calendar' && (
          <UpcomingPaymentsWidget onViewCalendar={() => setActiveView('calendar')} />
        )}
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-hidden">
        {activeView === 'list' && (
          <ResizablePanelGroup direction="horizontal" className="h-full">
            <ResizablePanel defaultSize={selectedLead ? 50 : 100} minSize={30}>
              <LeadList
                leads={filteredLeads}
                selectedLead={selectedLead}
                onSelectLead={handleLeadSelect}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                statusFilter={statusFilter}
                onStatusFilterChange={setStatusFilter}
                onLeadsDeleted={fetchData}
              />
            </ResizablePanel>

            {selectedLead && (
              <>
                <ResizableHandle withHandle />
                <ResizablePanel defaultSize={50} minSize={30}>
                  <LeadDetailPanel
                    lead={selectedLead}
                    onClose={() => setSelectedLead(null)}
                    onUpdate={handleLeadUpdate}
                    onDelete={handleLeadDelete}
                    onFollowupCreated={handleFollowupCreated}
                  />
                </ResizablePanel>
              </>
            )}
          </ResizablePanelGroup>
        )}

        {activeView === 'kanban' && (
          <ResizablePanelGroup direction="horizontal" className="h-full">
            <ResizablePanel defaultSize={selectedLead ? 65 : 100} minSize={50}>
              <KanbanView onSelectLead={handleLeadSelect} />
            </ResizablePanel>

            {selectedLead && (
              <>
                <ResizableHandle withHandle />
                <ResizablePanel defaultSize={35} minSize={25}>
                  <LeadDetailPanel
                    lead={selectedLead}
                    onClose={() => setSelectedLead(null)}
                    onUpdate={handleLeadUpdate}
                    onDelete={handleLeadDelete}
                    onFollowupCreated={handleFollowupCreated}
                  />
                </ResizablePanel>
              </>
            )}
          </ResizablePanelGroup>
        )}

        {activeView === 'calendar' && (
          <CalendarView />
        )}

        {activeView === 'reminders' && (
          <PaymentRemindersView />
        )}

        {activeView === 'tasks' && (
          <TasksView />
        )}

        {activeView === 'ai' && (
          <AIAssistantView />
        )}
      </div>

      <ImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={handleImport}
      />

      <AddLeadModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onCreated={handleLeadCreated}
      />

      <SettingsModal
        open={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
      />

      <AdminModal
        open={showAdminModal}
        onClose={() => setShowAdminModal(false)}
      />
    </div>
  );
}
